import type { LyricResult } from '@creanizer/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Card } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { BookOpen, Music, Mic2, FileText, Copy, Download } from 'lucide-react'

interface LyricResultTabsProps {
  result: LyricResult
  onCopy: (text: string, section?: string) => void
  onDownload: () => void
  copiedSection: string | null
}

export function LyricResultTabs({ 
  result, 
  onCopy, 
  onDownload,
  copiedSection 
}: LyricResultTabsProps) {
  return (
    <Tabs defaultValue="lyrics" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="lyrics">
          <BookOpen className="w-4 h-4 mr-2" />
          歌詞
        </TabsTrigger>
        <TabsTrigger value="structure">
          <Music className="w-4 h-4 mr-2" />
          構造
        </TabsTrigger>
        <TabsTrigger value="analysis">
          <Mic2 className="w-4 h-4 mr-2" />
          分析
        </TabsTrigger>
        <TabsTrigger value="suggestions">
          <FileText className="w-4 h-4 mr-2" />
          提案
        </TabsTrigger>
      </TabsList>

      {/* 歌詞タブ */}
      <TabsContent value="lyrics" className="mt-4">
        <Card className="p-4">
          <div className="flex justify-end mb-4 gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCopy(result.content.fullLyric)}
            >
              <Copy className="h-4 w-4 mr-2" />
              全体をコピー
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              ダウンロード
            </Button>
          </div>
          
          <ScrollArea className="h-[500px] w-full rounded-md border p-4">
            <div className="space-y-6">
              {result.content.sections.map((section, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between sticky top-0 bg-white py-2">
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      {section.label}
                    </h3>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onCopy(section.lyrics.join('\n'), section.label)}
                    >
                      {copiedSection === section.label ? (
                        <span className="text-green-600">コピー済み</span>
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <div className="pl-4 space-y-1">
                    {section.lyrics.map((line, lineIdx) => (
                      <p key={lineIdx} className="text-sm leading-relaxed">
                        {line}
                      </p>
                    ))}
                    {section.translation && section.translation.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">翻訳:</p>
                        {section.translation.map((line, lineIdx) => (
                          <p key={lineIdx} className="text-xs text-muted-foreground italic">
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </Card>
      </TabsContent>

      {/* 構造タブ */}
      <TabsContent value="structure" className="mt-4">
        <Card className="p-6 space-y-4">
          <div>
            <h3 className="font-semibold mb-2">曲の構成</h3>
            <p className="text-sm text-muted-foreground">
              {result.structure.format}
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3">セクション詳細</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {result.structure.sections.map((section, idx) => (
                <Card key={idx} className="p-3">
                  <p className="font-medium text-sm">{section.label}</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {section.lineCount}行
                    </p>
                    {section.syllableCount && (
                      <p className="text-xs text-muted-foreground">
                        {section.syllableCount}音節
                      </p>
                    )}
                    {section.rhymePattern && (
                      <Badge variant="outline" className="text-xs">
                        韻: {section.rhymePattern}
                      </Badge>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {result.musicalInfo && (
            <div>
              <h3 className="font-semibold mb-3">音楽的情報</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">テンポ:</span> {result.musicalInfo.tempo}
                </div>
                <div>
                  <span className="text-muted-foreground">拍子:</span> {result.musicalInfo.timeSignature}
                </div>
                {result.musicalInfo.suggestedKey && (
                  <div>
                    <span className="text-muted-foreground">推奨キー:</span> {result.musicalInfo.suggestedKey}
                  </div>
                )}
                {result.musicalInfo.vocalRange && (
                  <div>
                    <span className="text-muted-foreground">音域:</span> {result.musicalInfo.vocalRange}
                  </div>
                )}
                {result.musicalInfo.dynamicRange && (
                  <div>
                    <span className="text-muted-foreground">ダイナミクス:</span> {result.musicalInfo.dynamicRange}
                  </div>
                )}
                {result.musicalInfo.harmonicProgression && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">コード進行:</span> {result.musicalInfo.harmonicProgression}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">総行数:</span>
              <span className="font-medium">{result.structure.totalLines}行</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">推定演奏時間:</span>
              <span className="font-medium">{result.structure.estimatedDuration}</span>
            </div>
          </div>
        </Card>
      </TabsContent>

      {/* 分析タブ */}
      <TabsContent value="analysis" className="mt-4">
        <Card className="p-6 space-y-4">
          {/* 文体分析 */}
          <div>
            <h3 className="font-semibold mb-2">文体</h3>
            <p className="text-sm">{result.analysis.writingStyle}</p>
          </div>

          {/* 感情分析 */}
          <div>
            <h3 className="font-semibold mb-2">感情の流れ</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge>{result.analysis.emotionalTone.primary}</Badge>
                {result.analysis.emotionalTone.secondary && (
                  <>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline">{result.analysis.emotionalTone.secondary}</Badge>
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {result.analysis.emotionalTone.progression}
              </p>
            </div>
          </div>

          {/* テーマ分析 */}
          <div>
            <h3 className="font-semibold mb-2">テーマ</h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium">メイン:</span>
                <Badge className="ml-2">{result.analysis.themes.main}</Badge>
              </div>
              {result.analysis.themes.sub.length > 0 && (
                <div>
                  <span className="text-sm font-medium">サブ:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.analysis.themes.sub.map((theme, idx) => (
                      <Badge key={idx} variant="outline">{theme}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.analysis.themes.symbols.length > 0 && (
                <div>
                  <span className="text-sm font-medium">シンボル:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.analysis.themes.symbols.map((symbol, idx) => (
                      <Badge key={idx} variant="secondary">{symbol}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 韻の分析 */}
          <div>
            <h3 className="font-semibold mb-2">韻の構成</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">構成:</span> {result.analysis.rhymeAnalysis.scheme}
              </div>
              <div>
                <span className="text-muted-foreground">一貫性:</span> {result.analysis.rhymeAnalysis.consistency}
              </div>
              {result.analysis.rhymeAnalysis.internalRhymes && (
                <div>
                  <Badge variant="outline" className="text-xs">内部韻あり</Badge>
                </div>
              )}
              {result.analysis.rhymeAnalysis.slantRhymes && (
                <div>
                  <Badge variant="outline" className="text-xs">スラント韻あり</Badge>
                </div>
              )}
            </div>
          </div>

          {/* 語彙分析 */}
          <div>
            <h3 className="font-semibold mb-2">語彙</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">レベル:</span> {result.analysis.vocabulary.level}
              </div>
              <div>
                <span className="text-muted-foreground">ユニーク語数:</span> {result.analysis.vocabulary.uniqueWords}
              </div>
              <div>
                <span className="text-muted-foreground">総語数:</span> {result.analysis.vocabulary.totalWords}
              </div>
              {result.analysis.vocabulary.readabilityScore && (
                <div>
                  <span className="text-muted-foreground">可読性スコア:</span> {result.analysis.vocabulary.readabilityScore}
                </div>
              )}
            </div>
          </div>

          {/* 文学的技法 */}
          {result.analysis.literaryDevices && (
            <div>
              <h3 className="font-semibold mb-2">文学的技法</h3>
              <div className="space-y-2 text-sm">
                {result.analysis.literaryDevices.metaphors.length > 0 && (
                  <div>
                    <span className="font-medium">比喩:</span>
                    <ul className="list-disc list-inside mt-1 text-muted-foreground">
                      {result.analysis.literaryDevices.metaphors.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.analysis.literaryDevices.similes.length > 0 && (
                  <div>
                    <span className="font-medium">直喩:</span>
                    <ul className="list-disc list-inside mt-1 text-muted-foreground">
                      {result.analysis.literaryDevices.similes.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.analysis.literaryDevices.alliteration.length > 0 && (
                  <div>
                    <span className="font-medium">頭韻:</span>
                    <ul className="list-disc list-inside mt-1 text-muted-foreground">
                      {result.analysis.literaryDevices.alliteration.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </TabsContent>

      {/* 提案タブ */}
      <TabsContent value="suggestions" className="mt-4">
        <Card className="p-6">
          {result.suggestions ? (
            <div className="space-y-4">
              {result.suggestions.alternativeLines && result.suggestions.alternativeLines.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">代替案</h3>
                  <div className="space-y-3">
                    {result.suggestions.alternativeLines.map((alt, idx) => (
                      <Card key={idx} className="p-3">
                        <p className="text-sm line-through text-muted-foreground">
                          {alt.original}
                        </p>
                        <p className="text-sm font-medium mt-1">
                          → {alt.suggestion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          理由: {alt.reason}
                        </p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {result.suggestions.structuralSuggestions && result.suggestions.structuralSuggestions.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">構造の提案</h3>
                  <ul className="space-y-2">
                    {result.suggestions.structuralSuggestions.map((suggestion, idx) => (
                      <li key={idx} className="text-sm flex items-start">
                        <span className="text-muted-foreground mr-2">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.suggestions.melodicTips && result.suggestions.melodicTips.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">メロディのヒント</h3>
                  <ul className="space-y-2">
                    {result.suggestions.melodicTips.map((tip, idx) => (
                      <li key={idx} className="text-sm flex items-start">
                        <span className="text-muted-foreground mr-2">♪</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.suggestions.performanceNotes && result.suggestions.performanceNotes.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">パフォーマンスノート</h3>
                  <ul className="space-y-2">
                    {result.suggestions.performanceNotes.map((note, idx) => (
                      <li key={idx} className="text-sm flex items-start">
                        <span className="text-muted-foreground mr-2">🎤</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              この歌詞に対する提案はありません
            </p>
          )}
        </Card>
      </TabsContent>
    </Tabs>
  )
}